import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { size, filter, forEach, extend } from "lodash";
import { SortableContainer, SortableElement, DragHandle } from "@redash/viz/lib/components/sortable";
import location from "@/services/location";
import { Parameter, createParameter } from "@/services/parameters";
import ParameterApplyButton from "@/components/parameters/ParameterApplyButton";
import ParameterValueInput from "@/components/parameters/ParameterValueInput";
import PlainButton from "@/components/plain-button/PlainButton";
import EditParameterSettingsDialog from "../edit-parameter-settings-dialog/EditParameterSettingsDialog";
import { toHuman } from "@/lib/utils";

import "./Parameters.less";

function updateUrl(parameters) {
  const params = extend({}, location.search);
  parameters.forEach(param => {
    extend(params, param.toUrlParams());
  });
  location.setSearch(params, true);
}

function Parameters({
  parameters: initialParameters,
  editable,
  sortable,
  disableUrlUpdate,
  onValuesChange,
  onPendingValuesChange,
  onParametersEdit,
  appendSortableToParent,
}) {
  const [parameters, setParameters] = useState(initialParameters);

  useEffect(() => {
    if (!disableUrlUpdate) {
      updateUrl(parameters);
    }
  }, []);

  useEffect(() => {
    setParameters(initialParameters);
    if (!disableUrlUpdate) {
      updateUrl(initialParameters);
    }
  }, [initialParameters, disableUrlUpdate]);

  const handleKeyDown = useCallback(e => {
    // Cmd/Ctrl/Alt + Enter
    if (e.keyCode === 13 && (e.ctrlKey || e.metaKey || e.altKey)) {
      e.stopPropagation();
      applyChanges();
    }
  }, []);

  const setPendingValue = useCallback((param, value, isDirty) => {
    setParameters(prevParameters => {
      const updatedParameters = prevParameters.map(p => {
        if (p === param) {
          if (isDirty) {
            p.setPendingValue(value);
          } else {
            p.clearPendingValue();
          }
        }
        return p;
      });
      onPendingValuesChange();
      return updatedParameters;
    });
  }, [onPendingValuesChange]);

  const moveParameter = useCallback(({ oldIndex, newIndex }) => {
    if (oldIndex !== newIndex) {
      setParameters(prevParameters => {
        const updatedParameters = [...prevParameters];
        updatedParameters.splice(newIndex, 0, updatedParameters.splice(oldIndex, 1)[0]);
        onParametersEdit(updatedParameters);
        return updatedParameters;
      });
    }
  }, [onParametersEdit]);

  const applyChanges = useCallback(() => {
    setParameters(prevParameters => {
      const parametersWithPendingValues = prevParameters.filter(p => p.hasPendingValue);
      forEach(prevParameters, p => p.applyPendingValue());
      if (!disableUrlUpdate) {
        updateUrl(prevParameters);
      }
      onValuesChange(parametersWithPendingValues);
      return [...prevParameters];
    });
  }, [disableUrlUpdate, onValuesChange]);

  const showParameterSettings = useCallback((parameter, index) => {
    EditParameterSettingsDialog.showModal({ parameter }).onClose(updated => {
      setParameters(prevParameters => {
        const updatedParameters = [...prevParameters];
        const updatedParameter = extend(parameter, updated);
        updatedParameters[index] = createParameter(updatedParameter, updatedParameter.parentQueryId);
        onParametersEdit(updatedParameters);
        return updatedParameters;
      });
    });
  }, [onParametersEdit]);

  const renderParameter = useCallback((param, index) => {
    return (
      <div key={param.name} className="di-block" data-test={`ParameterName-${param.name}`}>
        <div className="parameter-heading">
          <label>{param.title || toHuman(param.name)}</label>
          {editable && (
            <PlainButton
              className="btn btn-default btn-xs m-l-5"
              aria-label="Edit"
              onClick={() => showParameterSettings(param, index)}
              data-test={`ParameterSettings-${param.name}`}
              type="button">
              <i className="fa fa-cog" aria-hidden="true" />
            </PlainButton>
          )}
        </div>
        <ParameterValueInput
          type={param.type}
          value={param.normalizedValue}
          parameter={param}
          enumOptions={param.enumOptions}
          queryId={param.queryId}
          onSelect={(value, isDirty) => setPendingValue(param, value, isDirty)}
        />
      </div>
    );
  }, [editable, showParameterSettings, setPendingValue]);

  const dirtyParamCount = size(filter(parameters, "hasPendingValue"));

  return (
    <SortableContainer
      disabled={!sortable}
      axis="xy"
      useDragHandle
      lockToContainerEdges
      helperClass="parameter-dragged"
      helperContainer={containerEl => (appendSortableToParent ? containerEl : document.body)}
      onSortEnd={moveParameter}
      containerProps={{
        className: "parameter-container",
        onKeyDown: dirtyParamCount ? handleKeyDown : null,
      }}>
      {parameters.map((param, index) => (
        <SortableElement key={param.name} index={index}>
          <div
            className="parameter-block"
            data-editable={sortable || null}
            data-test={`ParameterBlock-${param.name}`}>
            {sortable && <DragHandle data-test={`DragHandle-${param.name}`} />}
            {renderParameter(param, index)}
          </div>
        </SortableElement>
      ))}
      <ParameterApplyButton onClick={applyChanges} paramCount={dirtyParamCount} />
    </SortableContainer>
  );
}

Parameters.propTypes = {
  parameters: PropTypes.arrayOf(PropTypes.instanceOf(Parameter)),
  editable: PropTypes.bool,
  sortable: PropTypes.bool,
  disableUrlUpdate: PropTypes.bool,
  onValuesChange: PropTypes.func,
  onPendingValuesChange: PropTypes.func,
  onParametersEdit: PropTypes.func,
  appendSortableToParent: PropTypes.bool,
};

Parameters.defaultProps = {
  parameters: [],
  editable: false,
  sortable: false,
  disableUrlUpdate: false,
  onValuesChange: () => {},
  onPendingValuesChange: () => {},
  onParametersEdit: () => {},
  appendSortableToParent: true,
};

export default Parameters;
